import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Menu } from "lucide-react";
import IndependenceDayBanner from "@/components/IndependenceDayBanner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const NAV_LINKS = [
  ["About Us", "/about"],
  ["Buy", "/buy"],
  ["Rent", "/rent"],
  ["Sell", "/sell"],
  ["New Launches", "/new-launches"],
  ["For Agents", "/login"],
  ["Financials", "/financials"],
  ["Market Insights", "/market-insights"],
];

export default function PublicNav({ contactHref = "/contact" }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close the sheet whenever the user navigates.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <IndependenceDayBanner />
      <header className="bg-sg-navy text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="/inpropportal-logo.png"
              alt="INPropPortal.com"
              className="h-11 w-auto object-contain"
              data-testid="brand-logo-img"
            />
            <span className="sr-only">INPropPortal.com</span>
          </Link>

          {/* Desktop nav — lg and up */}
          <nav className="hidden lg:flex items-center gap-7 text-sm">
            {NAV_LINKS.map(([label, to]) => (
              <Link
                key={label}
                to={to}
                className="text-white/85 hover:text-sg-gold transition-colors"
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Desktop-only Contact pill */}
            <Link
              to={contactHref}
              className="hidden md:inline-flex items-center gap-2 rounded-full border border-sg-gold/70 px-4 py-2 text-sm text-sg-gold hover:bg-sg-gold hover:text-sg-navy transition-colors"
              data-testid="nav-contact"
            >
              <Mail className="w-4 h-4" /> Contact Us
            </Link>

            {/* Mobile hamburger — visible below lg */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-sg-gold/70 text-sg-gold hover:bg-sg-gold hover:text-sg-navy transition-colors"
                  aria-label="Open navigation menu"
                  data-testid="nav-mobile-toggle"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[86%] sm:w-[380px] bg-sg-navy text-white border-l border-sg-gold/25 p-0 flex flex-col"
                data-testid="nav-mobile-sheet"
              >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10 text-left">
                  <SheetTitle className="text-white font-display text-2xl">Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex-1 overflow-y-auto px-2 py-4">
                  {NAV_LINKS.map(([label, to]) => (
                    <SheetClose asChild key={label}>
                      <Link
                        to={to}
                        className="block px-4 py-3 rounded-xl text-base text-white/90 hover:bg-white/10 hover:text-sg-gold transition-colors min-h-[44px]"
                        data-testid={`nav-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                  <SheetClose asChild>
                    <Link
                      to={contactHref}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-sg-gold/70 px-4 py-3 text-sm text-sg-gold hover:bg-sg-gold hover:text-sg-navy transition-colors min-h-[44px]"
                      data-testid="nav-mobile-contact"
                    >
                      <Mail className="w-4 h-4" /> Contact Us
                    </Link>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
}
